import {
    Box,
    Button,
    Typography,
    Grid,
    TextField,
    CircularProgress,
    MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
    useAsignarPrestadorMutation,
    useAsignarTurnoExamenEmpleadosMutation,
    useGetPrestadoresQuery
} from '../../../services/periodicosApi';
import dayjs from 'dayjs';
import DateTimePickerComponent from '../../../components/DateTimePickerComponent';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);
interface PeriodicosDrawerProps {
    selectedIds: string[];
    setOpen: (state: string) => void;
    reloadGrid: () => void;
    selectedEmployee: any;
    reloadCurrentTab: () => void;
    tabIndex: number;
}
const PeriodicosAsignarTurnoDrawer = ({
    selectedIds,
    setOpen,
    reloadGrid,
    selectedEmployee,
    reloadCurrentTab,
    tabIndex
}: PeriodicosDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const [fechaTurno, setFechaTurno] = useState<any>(null);
    const { enqueueSnackbar } = useSnackbar();
    const [selectedPrestadorId, setSelectedPrestadorId] = useState<number | null>(null);
    const [asignarTurno, { isLoading }] = useAsignarTurnoExamenEmpleadosMutation();
    const [asignarPrestador] = useAsignarPrestadorMutation();
    const { data: prestadores = [] } = useGetPrestadoresQuery({});
    const isMassiveAssignment = selectedIds.length > 1;

    const prestadoresOptionsFormatted =
        prestadores?.map((p) => ({
            id: p.id,
            nombre: p.razonSocial
        })) || [];

    const idEstadoEmpleadoExpuesto = selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto;

    const isReadOnly = selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto === 5;

    const handleCloseDrawer = () => {
        setOpen('');
        if (tabIndex === 3) {
            reloadGrid();
        }
    };
    useEffect(() => {
        if (selectedEmployee?.examenEmpleado?.fechaHoraTurno) {
            const rawFecha = selectedEmployee.examenEmpleado.fechaHoraTurno;
            const parsedFecha = dayjs(rawFecha, 'YYYY-MM-DDTHH:mm:ss');
            setFechaTurno(parsedFecha);
        }
        if (selectedEmployee?.examenEmpleado?.prestador?.id) {
            setSelectedPrestadorId(selectedEmployee.examenEmpleado.prestador.id);
        }
    }, [selectedEmployee]);

    const handleSave = async () => {
        const idExamenEmpleados = selectedIds.map((id: string) => parseInt(id.split('-')[0], 10));

        if (!fechaTurno || (!selectedPrestadorId && !isMassiveAssignment)) {
            enqueueSnackbar(t('drawer.messages.missingData'), { variant: 'error' });
            return;
        }

        try {
            if (!isMassiveAssignment) {
                const currentPrestadorId = selectedEmployee?.examenEmpleado?.prestador?.id;
                if (selectedPrestadorId !== currentPrestadorId) {
                    await asignarPrestador({
                        idExamenEmpleados,
                        idPrestador: selectedPrestadorId!
                    }).unwrap();
                }
            }

            await asignarTurno({
                idExamenEmpleados,
                fechaTurno: dayjs(fechaTurno).format('YYYY-MM-DDTHH:mm:ss')
            }).unwrap();

            enqueueSnackbar(t('drawer.messages.assignSuccessTurn'), { variant: 'success' });
            reloadGrid();
            setOpen('');
        } catch (error) {
            enqueueSnackbar(t('drawer.messages.assignErrorTurn'), { variant: 'error' });
            console.error(error);
        }
    };

    const currentPrestador = prestadores.find((p) => p.id === selectedPrestadorId);

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Grid container flexDirection="column">
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={
                        isMassiveAssignment
                            ? t('drawer.turno.massiveTitle')
                            : `${selectedEmployee?.examenEmpleado?.riesgo?.codigo || ''} ${
                                  selectedEmployee?.examenEmpleado?.riesgo?.descripcion || ''
                              }`
                    }
                />

                {!isMassiveAssignment && (
                    <>
                        <Typography sx={{ fontWeight: 'bold', mb: 2, mt: 4 }}>
                            {t('drawer.exam.subtitle')}
                        </Typography>

                        <Box>
                            <Typography variant="body1" fontWeight="bold">
                                {selectedEmployee?.examenEmpleado?.examen?.descripcion || '-'}
                            </Typography>
                            <Typography variant="body2" mt={1}>
                                <Box component="span" fontWeight="bold">
                                    {t('drawer.exam.frecuency')}
                                </Box>{' '}
                                {selectedEmployee?.examenEmpleado?.examen?.frecuencia || '-'}
                            </Typography>
                        </Box>

                        <Typography sx={{ fontWeight: 'bold', mb: 2, mt: 4 }}>
                            {t('drawer.prestador.subtitle')}
                        </Typography>

                        <AutocompleteComponent
                            label={t('drawer.prestador.subtitle')}
                            name="idPrestador"
                            value={
                                prestadoresOptionsFormatted.find(
                                    (p) => p.id === selectedPrestadorId
                                ) || null
                            }
                            onChange={(value: any) => {
                                setSelectedPrestadorId(value?.id || null);
                            }}
                            disabled={isReadOnly}
                            options={prestadoresOptionsFormatted}
                            clearable
                            fullWidth
                        />

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: '8px',
                                backgroundColor: '#E0EBED'
                            }}
                        >
                            <Typography variant="body1" fontWeight="bold">
                                {selectedEmployee?.examenEmpleado?.prestador?.razonSocial || '-'}
                            </Typography>
                            <Typography variant="body2">
                                <Box component="span" fontWeight="bold">
                                    {t('drawer.prestador.address')}
                                </Box>{' '}
                                {selectedEmployee?.examenEmpleado?.prestador?.address || '-'}
                            </Typography>
                            <Typography variant="body2">
                                <Box component="span" fontWeight="bold">
                                    {t('drawer.prestador.cuit')}
                                </Box>{' '}
                                {selectedEmployee?.examenEmpleado?.prestador?.cuit || '-'}
                            </Typography>
                            <Typography variant="body2">
                                <Box component="span" fontWeight="bold">
                                    {t('drawer.prestador.phone')}
                                </Box>{' '}
                                {selectedEmployee?.examenEmpleado?.prestador?.telefono || '-'}
                            </Typography>
                            <Typography variant="body2">
                                <Box component="span" fontWeight="bold">
                                    {t('drawer.prestador.mail')}
                                </Box>{' '}
                                {selectedEmployee?.examenEmpleado?.prestador?.email || '-'}
                            </Typography>
                        </Box>
                    </>
                )}

                <Typography sx={{ fontWeight: 'bold', mb: 2, mt: 6 }}>
                    {t('drawer.turno.title')}
                </Typography>

                <DateTimePickerComponent
                    label={t('drawer.turno.title')}
                    value={fechaTurno}
                    onChange={setFechaTurno}
                    disabled={isReadOnly}
                    disablePast
                    sx={{ mt: 2, width: '300px' }}
                />
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={handleCloseDrawer} sx={{ mr: 1 }}>
                    {t('drawer.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!fechaTurno || isLoading}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('drawer.save')}
                </Button>
            </Box>
        </Box>
    );
};
export default PeriodicosAsignarTurnoDrawer;
